import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Zap, Play, Pause, Trash2, ChevronRight, Clock, Mail, Phone, Linkedin, MessageSquare, CalendarCheck, ListTodo, Loader2, RefreshCw, ArrowRight, Brain, CheckCircle2, AlertTriangle, Lock } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatLocal } from '../lib/dateUtils';
import { parsePlanError } from '../lib/planError';

const STEP_ICONS = {
  email: Mail,
  call: Phone,
  linkedin: Linkedin,
  sms: MessageSquare,
  meeting_request: CalendarCheck,
  task: ListTodo,
};

const STEP_COLORS = {
  email: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  call: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  linkedin: 'bg-sky-500/10 text-sky-500 border-sky-500/20',
  sms: 'bg-violet-500/10 text-violet-500 border-violet-500/20',
  meeting_request: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  task: 'bg-rose-500/10 text-rose-500 border-rose-500/20',
};

// Mirrors backend services/channel_availability.channel_of — maps a step type
// to the channel that must be connected for it to actually deliver.
const CHANNEL_OF = (type) => {
  const t = (type || '').toLowerCase();
  if (['email', 'email_draft'].includes(t)) return 'email';
  if (['call', 'voice', 'ai_call'].includes(t)) return 'call';
  if (['whatsapp', 'wa'].includes(t)) return 'whatsapp';
  if (['linkedin', 'linkedin_note'].includes(t)) return 'linkedin';
  if (['sms', 'text'].includes(t)) return 'sms';
  return 'internal';
};
const CHANNEL_LABEL = { whatsapp: 'WhatsApp', sms: 'SMS' };

export function SmartCadencePanel({ leadId, leadName, leadNeedsReview = false, refreshKey, onApproved }) {
  const [cadences, setCadences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [needsReview, setNeedsReview] = useState(leadNeedsReview);
  const [prevNeedsReviewProp, setPrevNeedsReviewProp] = useState(leadNeedsReview);
  const [detailCadence, setDetailCadence] = useState(null);
  const [planGate, setPlanGate] = useState(null);
  const [connectedChannels, setConnectedChannels] = useState(null);

  // Sync prop → local state during render (React-recommended; avoids effect cascade)
  if (prevNeedsReviewProp !== leadNeedsReview) {
    setPrevNeedsReviewProp(leadNeedsReview);
    setNeedsReview(leadNeedsReview);
  }

  const loadCadences = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/brain/cadences?lead_id=${leadId}`);
      setCadences(res.data?.cadences || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (!leadId) return undefined;
    let cancelled = false;
    api.get(`/api/brain/cadences?lead_id=${leadId}`)
      .then((res) => { if (!cancelled) setCadences(res.data?.cadences || []); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [leadId, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    api.get('/api/brain/cadences/connected-channels')
      .then((res) => { if (!cancelled) setConnectedChannels(res.data?.channels || null); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  const handleGenerate = async () => {
    if (cadences.some(c => c.status === 'active' || c.status === 'paused')) {
      toast.error('This lead already has an active cadence. Complete or stop it first.');
      return;
    }
    setGenerating(true);
    try {
      const res = await api.post(`/api/brain/cadences/generate/${leadId}`);
      setCadences(prev => [res.data, ...prev]);
      setPlanGate(null);
      toast.success('Smart cadence generated');
    } catch (err) {
      const gate = parsePlanError(err);
      if (gate) {
        setPlanGate(gate);          // render inline upgrade banner
        toast.error(gate.message);
      } else {
        toast.error(err?.response?.data?.detail || 'Failed to generate cadence');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleStatus = async (cadenceId) => {
    try {
      const res = await api.put(`/api/brain/cadences/${cadenceId}/status`);
      setCadences(prev => prev.map(c => c.id === cadenceId ? { ...c, status: res.data.status } : c));
      toast.success(`Cadence ${res.data.status}`);
    } catch {
      toast.error('Failed to update cadence');
    }
  };

  const handleAdvance = async (cadenceId) => {
    try {
      const res = await api.put(`/api/brain/cadences/${cadenceId}/advance`);
      setCadences(prev => prev.map(c => c.id === cadenceId ? { ...c, current_step: res.data.current_step, status: res.data.status } : c));
      if (res.data.completed) {
        toast.success('Cadence completed!');
      } else {
        toast.success('Step completed, advancing...');
      }
    } catch {
      toast.error('Failed to advance step');
    }
  };

  const handleApproveCadence = async () => {
    setApproving(true);
    try {
      const res = await api.post(`/api/leads/${leadId}/approve-cadence`);
      const msg = res?.data?.message || 'Cadence approved — Brain will resume outreach';
      toast.success(msg);
      setNeedsReview(false);
      // Notify parent so the Lead detail header re-fetches the lead doc
      if (onApproved) {
        try { onApproved(); } catch { /* non-blocking */ }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to approve cadence';
      toast.error(detail);
    } finally {
      setApproving(false);
    }
  };

  const handleDelete = async (cadenceId) => {
    try {
      await api.delete(`/api/brain/cadences/${cadenceId}`);
      setCadences(prev => prev.filter(c => c.id !== cadenceId));
      toast.success('Cadence deleted');
    } catch {
      toast.error('Failed to delete cadence');
    }
  };

  const handleRunBrain = async () => {
    setExecuting(true);
    try {
      const res = await api.post('/api/brain/cadences/execute-now');
      const data = res.data;
      if (data.executed > 0 || data.skipped > 0 || data.exited > 0) {
        toast.success(`Brain executed: ${data.executed} steps, ${data.skipped} skipped, ${data.exited} exited`);
      } else {
        toast.info('No cadence steps were due for execution');
      }
      await loadCadences();
    } catch {
      toast.error('Failed to run brain executor');
    } finally {
      setExecuting(false);
    }
  };

  return (
    <Card className="border-violet-500/20" data-testid="smart-cadence-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-1">
          <CardTitle className="text-sm font-semibold flex items-center gap-1.5 min-w-0">
            <Zap className="h-4 w-4 text-violet-500 shrink-0" />
            <span className="truncate">Smart Cadence</span>
          </CardTitle>
          <div className="flex items-center gap-1 shrink-0">
            {cadences.some(c => c.status === 'active') && (
              <Button variant="outline" size="icon" className="h-7 w-7 border-violet-500/30 text-violet-500"
                onClick={handleRunBrain} disabled={executing} data-testid="run-brain-cadence-btn" title="Run Brain">
                {executing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Brain className="h-3.5 w-3.5" />}
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={loadCadences} data-testid="cadence-refresh-btn" title="Refresh">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon" className="h-7 w-7 bg-violet-600 hover:bg-violet-700 text-white"
              onClick={handleGenerate}
              disabled={generating || cadences.some(c => c.status === 'active' || c.status === 'paused')}
              data-testid="generate-cadence-btn"
              title={cadences.some(c => c.status === 'active' || c.status === 'paused') ? 'This lead already has an active cadence' : 'Generate Cadence'}>
              {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Plan/limit upgrade wall — replaces the old generic "Failed to generate"
            toast with a real reason + upgrade CTA (Iter 62.9 Phase 0). */}
        {planGate && (
          <div
            data-testid="cadence-plan-gate-banner"
            className="flex items-start gap-3 p-3 rounded-md border border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300"
          >
            <Lock className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold">
                {planGate.requiredPlan
                  ? `${planGate.requiredPlan.charAt(0).toUpperCase() + planGate.requiredPlan.slice(1)} plan feature`
                  : 'Cadence limit reached'}
              </p>
              <p className="text-[11px] mt-0.5 opacity-80">{planGate.message}</p>
            </div>
            <Button
              size="sm"
              className="h-7 px-3 text-xs bg-violet-600 hover:bg-violet-700 text-white shrink-0"
              onClick={() => { window.location.href = planGate.upgradeUrl; }}
              data-testid="cadence-upgrade-btn"
            >
              Upgrade
            </Button>
          </div>
        )}

        {/* Bronze / paused-cadence banner — surfaces the "Approve to Send" CTA
            promised by the Pipeline banner + Cadence Analytics empty-state. */}
        {needsReview && (
          <div
            data-testid="cadence-needs-review-banner"
            className="flex items-start gap-3 p-3 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold">Outreach paused — niche ICP match</p>
              <p className="text-[11px] mt-0.5 opacity-80">
                AI Brain flagged this lead for human review (low ICP fit score).
                Approve to start the auto-cadence.
              </p>
            </div>
            <Button
              size="sm"
              className="h-7 px-3 text-xs bg-amber-600 hover:bg-amber-700 text-white shrink-0"
              onClick={handleApproveCadence}
              disabled={approving}
              data-testid="approve-cadence-btn"
            >
              {approving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Approve to Send
            </Button>
          </div>
        )}

        {loading ? (
          <div className="text-xs text-muted-foreground text-center py-4">Loading cadences...</div>
        ) : cadences.length === 0 ? (
          <div className="text-center py-4 space-y-2">
            <p className="text-xs text-muted-foreground">No cadences yet</p>
            <p className="text-xs text-muted-foreground">Generate an AI-powered follow-up sequence for {leadName || 'this lead'}</p>
          </div>
        ) : (
          cadences.map(cadence => (
            <CadenceCard key={cadence.id} cadence={cadence}
              onToggle={() => handleToggleStatus(cadence.id)}
              onAdvance={() => handleAdvance(cadence.id)}
              onDelete={() => handleDelete(cadence.id)}
              onViewDetail={() => setDetailCadence(cadence)}
            />
          ))
        )}
      </CardContent>

      {/* Cadence Detail Dialog */}
      <Dialog open={!!detailCadence} onOpenChange={() => setDetailCadence(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="cadence-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-violet-500" />
              {detailCadence?.cadence_name}
            </DialogTitle>
          </DialogHeader>
          {detailCadence && <CadenceDetail cadence={detailCadence} connectedChannels={connectedChannels} />}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function getCadenceStats(cadence) {
  // Onboarding-generated cadences lack total_steps / estimated_duration_days.
  // Derive them from the steps array so the modal never shows blanks.
  const steps = cadence.steps || [];
  const totalSteps = cadence.total_steps ?? steps.length ?? 0;
  let durationDays = cadence.estimated_duration_days;
  if (!durationDays && steps.length) {
    const maxDay = Math.max(0, ...steps.map((s) => Number(s.day) || 0));
    if (maxDay > 0) {
      durationDays = maxDay;
    } else {
      const totalHours = steps.reduce((sum, s) => sum + (Number(s.delay_hours) || 0), 0);
      durationDays = totalHours > 0 ? Math.max(1, Math.ceil(totalHours / 24)) : steps.length;
    }
  }
  return { totalSteps: totalSteps || 0, durationDays: durationDays || 0 };
}

function CadenceCard({ cadence, onToggle, onAdvance, onDelete, onViewDetail }) {
  const { totalSteps } = getCadenceStats(cadence);
  const progressPct = totalSteps > 0 ? Math.round((cadence.current_step / totalSteps) * 100) : 0;
  const currentStepData = cadence.steps?.[cadence.current_step];

  return (
    <div className="border rounded-lg p-3 space-y-2 hover:border-violet-500/30 transition-colors" data-testid={`cadence-card-${cadence.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Badge variant="outline" className={`text-[10px] ${
            cadence.status === 'active' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30' :
            cadence.status === 'completed' ? 'bg-blue-500/10 text-blue-500 border-blue-500/30' :
            'bg-amber-500/10 text-amber-500 border-amber-500/30'
          }`} data-testid="cadence-status-badge">
            {cadence.status}
          </Badge>
          <span className="text-sm font-medium truncate">{cadence.cadence_name}</span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {cadence.status !== 'completed' && (
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onToggle} data-testid="cadence-toggle-btn">
              {cadence.status === 'active' ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={onDelete} data-testid="cadence-delete-btn">
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <div className="flex-1 bg-muted rounded-full h-1.5">
          <div className="bg-violet-500 h-1.5 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
        </div>
        <span>{cadence.current_step}/{totalSteps} steps</span>
      </div>

      {/* Current Step Preview */}
      {currentStepData && cadence.status === 'active' && (
        <div className="flex items-center justify-between text-xs bg-muted/50 rounded p-2">
          <div className="flex items-center gap-1.5">
            <StepIcon type={currentStepData.type} />
            <span className="font-medium">{currentStepData.title}</span>
          </div>
          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={onAdvance} data-testid="cadence-advance-btn">
            Complete <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      )}

      <Button variant="ghost" size="sm" className="w-full h-7 text-xs" onClick={onViewDetail} data-testid="cadence-view-detail-btn">
        View Details <ChevronRight className="h-3 w-3 ml-1" />
      </Button>
    </div>
  );
}

function CadenceDetail({ cadence, connectedChannels }) {
  const { totalSteps, durationDays } = getCadenceStats(cadence);
  return (
    <div className="space-y-4" data-testid="cadence-detail-content">
      <p className="text-sm text-muted-foreground">{cadence.strategy}</p>

      <div className="flex gap-4 text-xs">
        <div><span className="text-muted-foreground">Duration:</span> <span className="font-medium">{durationDays} days</span></div>
        <div><span className="text-muted-foreground">Steps:</span> <span className="font-medium">{totalSteps}</span></div>
        <div><span className="text-muted-foreground">Progress:</span> <span className="font-medium">{cadence.current_step}/{totalSteps}</span></div>
      </div>

      {/* Steps Timeline */}
      <div className="space-y-0">
        {cadence.steps?.map((step, idx) => {
          const isComplete = idx < cadence.current_step;
          const isCurrent = idx === cadence.current_step;
          const isAIExecuted = step.executed_by === 'ai_brain';
          return (
            <div key={idx} className="flex gap-3" data-testid={`cadence-step-${idx}`}>
              <div className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium border ${
                  isComplete ? 'bg-emerald-500 text-white border-emerald-500' :
                  isCurrent ? 'bg-violet-500 text-white border-violet-500' :
                  'bg-muted border-border text-muted-foreground'
                }`}>
                  {isComplete ? <CheckCircle2 className="h-3.5 w-3.5" /> : step.step_number}
                </div>
                {idx < cadence.steps.length - 1 && <div className="w-px h-8 bg-border" />}
              </div>
              <div className="flex-1 pb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <StepIcon type={step.type} />
                  <span className="text-sm font-medium">{step.title}</span>
                  <Badge variant="outline" className="text-[10px]">{step.priority}</Badge>
                  {(() => {
                    const ch = CHANNEL_OF(step.type);
                    if (['whatsapp', 'sms'].includes(ch) && connectedChannels && connectedChannels[ch] === false && !isComplete) {
                      return (
                        <a href="/integrations" data-testid={`step-needs-connection-${idx}`}
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 transition-colors">
                          <AlertTriangle className="h-2.5 w-2.5" /> Connect {CHANNEL_LABEL[ch] || ch}
                        </a>
                      );
                    }
                    return null;
                  })()}
                  {isAIExecuted && (
                    <Badge className="text-[10px] bg-violet-500/15 text-violet-500 border-violet-500/30" variant="outline" data-testid={`step-ai-executed-${idx}`}>
                      <Brain className="h-2.5 w-2.5 mr-0.5" /> AI Executed
                    </Badge>
                  )}
                  {isComplete && !isAIExecuted && (
                    <Badge className="text-[10px] bg-emerald-500/15 text-emerald-500 border-emerald-500/30" variant="outline">
                      Completed
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
                <div className="flex gap-3 mt-1 text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-1"><Clock className="h-2.5 w-2.5" />{step.delay_hours}h delay</span>
                  {step.goal && <span>Goal: {step.goal}</span>}
                  {step.executed_at && <span>Executed: {formatLocal(step.executed_at, 'MMM dd, HH:mm')}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Exit Conditions */}
      {cadence.exit_conditions?.length > 0 && (
        <div>
          <p className="text-xs font-medium mb-1">Exit Conditions</p>
          <div className="flex flex-wrap gap-1">
            {cadence.exit_conditions.map((cond, i) => (
              <Badge key={i} variant="secondary" className="text-[10px]">{cond}</Badge>
            ))}
          </div>
        </div>
      )}

      {/* Exit Reason (if cadence was exited by brain) */}
      {cadence.exit_reason && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded p-2">
          <p className="text-xs font-medium text-amber-600 flex items-center gap-1">
            <Brain className="h-3 w-3" /> Brain Exit Reason
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{cadence.exit_reason}</p>
        </div>
      )}
    </div>
  );
}

function StepIcon({ type }) {
  const Icon = STEP_ICONS[type] || ListTodo;
  const colorClass = STEP_COLORS[type] || STEP_COLORS.task;
  return (
    <span className={`inline-flex items-center justify-center h-5 w-5 rounded border ${colorClass}`}>
      <Icon className="h-3 w-3" />
    </span>
  );
}
